import { Link } from "react-router-dom";
import { Mail, MapPin, Phone } from "lucide-react";
import { EMAIL, PHONE_DISPLAY, PHONE_TEL, WA_LINK, scrollToId, useLang } from "../i18n";
import { WhatsAppLogo } from "./WhatsAppLogo";

export default function Footer() {
  const { t } = useLang();
  return (
    <footer className="border-t border-white/10 bg-[#0a0a0a] pb-8 pt-16">
      <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-[1.3fr_1fr_1.2fr_1fr]">
          <div>
            <a
              data-testid="footer-whatsapp-badge"
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="WhatsApp"
              className="flex h-11 w-11 items-center justify-center rounded-xl bg-wa"
            >
              <WhatsAppLogo className="h-6 w-6 text-white" />
            </a>
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-white/60">
              {t.footer.tagline}
            </p>
          </div>

          <div>
            <h4 className="text-xs font-extrabold tracking-[0.3em] text-gold">
              {t.footer.services}
            </h4>
            <ul className="mt-5 space-y-3">
              {t.services.items.map((s) => (
                <li key={s.t}>
                  <button
                    data-testid={`footer-service-link-${s.t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    onClick={() => scrollToId("services")}
                    className="text-left text-sm text-white/70 transition-colors hover:text-gold"
                  >
                    {s.t}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-extrabold tracking-[0.3em] text-gold">
              {t.footer.contact}
            </h4>
            <ul className="mt-5 space-y-3 text-sm text-white/70">
              <li data-testid="footer-address-item" className="flex items-center gap-2.5">
                <MapPin className="h-4 w-4 shrink-0 text-gold" />
                INA-Ring 12, 91074 Herzogenaurach
              </li>
              <li data-testid="footer-phone-link">
                <a href={PHONE_TEL} className="flex items-center gap-2.5 transition-colors hover:text-gold">
                  <Phone className="h-4 w-4 shrink-0 text-callope" />
                  {PHONE_DISPLAY}
                </a>
              </li>
              <li data-testid="footer-email-link">
                <a
                  href={`mailto:${EMAIL}`}
                  className="flex items-center gap-2.5 transition-colors hover:text-gold"
                >
                  <Mail className="h-4 w-4 shrink-0 text-gold" />
                  {EMAIL}
                </a>
              </li>
              <li data-testid="footer-whatsapp-link">
                <a
                  href={WA_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2.5 transition-colors hover:text-gold"
                >
                  <WhatsAppLogo className="h-4 w-4 shrink-0 text-wa" />
                  WhatsApp
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-extrabold tracking-[0.3em] text-gold">
              {t.footer.legal}
            </h4>
            <ul className="mt-5 space-y-3 text-sm text-white/70">
              <li>
                <Link
                  data-testid="footer-impressum-link"
                  to="/impressum"
                  className="transition-colors hover:text-gold"
                >
                  {t.footer.impressum}
                </Link>
              </li>
              <li>
                <Link
                  data-testid="footer-datenschutz-link"
                  to="/datenschutz"
                  className="transition-colors hover:text-gold"
                >
                  {t.footer.datenschutz}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-3 border-t border-white/10 pt-6 text-xs text-white/50 sm:flex-row">
          <span data-testid="footer-copyright">{t.footer.rights}</span>
          <a
            data-testid="footer-powered-by"
            href="https://www.yugtech.de"
            target="_blank"
            rel="noopener noreferrer"
            className="font-extrabold text-white/80 transition-colors hover:text-gold"
          >
            Powered by “Yugtech IT services and consulting” (www.yugtech.de)
          </a>
          <span data-testid="footer-cities">{t.footer.cities}</span>
        </div>
      </div>
    </footer>
  );
}
